<script setup>
import Dashboard from './Home/Dashboard.vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { computed } from 'vue'

const router = useRouter()
const userStore = useUserStore()

const isAuthenticated = computed(() => userStore.isAuthenticated)

const navigateToLogin = () => {
  router.push({ name: 'login'})
}

const navigateToRegister = () => {
  router.push('/register')
}

const navigateToMyBets = () => {
  router.push('/mybets')
}

const navigateToList = () => {
  router.push('/list')
}
</script>

<template>
  <main>
    <!-- Sezione Introduttiva -->
    <v-container fluid class="hero-section">
      <v-row class="align-center justify-center min-height-section">
        <v-col cols="12" md="8" lg="6">
          <div class="text-center">
            <!-- Logo/Icona principale -->
            <div class="mb-6">
              <v-icon size="80" color="primary" class="mb-4">mdi-gamepad-variant</v-icon>
              <h1 class="text-h2 font-weight-bold mb-4 gradient-text">{{ $t('home.title') }}</h1>
              <h2 class="text-h5 text-medium-emphasis mb-6">
                {{ $t('home.subtitle') }}
              </h2>
            </div>

            <!-- Descrizione -->
            <div class="mb-8">
              <p class="text-h6 mb-4 text-medium-emphasis">
                {{ $t('home.description') }}
              </p>
              
              <v-row class="mb-6">
                <v-col cols="12" sm="4">
                  <div class="feature-item" @click="navigateToList">
                    <v-icon size="32" color="primary" class="mb-2">mdi-soccer</v-icon>
                    <p class="text-subtitle-1 font-weight-medium">{{ $t('home.features.realEvents.title') }}</p>
                    <p class="text-caption text-medium-emphasis">{{ $t('home.features.realEvents.description') }}</p>
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="feature-item" @click="navigateToList">
                    <v-icon size="32" color="success" class="mb-2">mdi-school</v-icon>
                    <p class="text-subtitle-1 font-weight-medium">{{ $t('home.features.learnPlaying.title') }}</p>
                    <p class="text-caption text-medium-emphasis">{{ $t('home.features.learnPlaying.description') }}</p>
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="feature-item" @click="navigateToList">
                    <v-icon size="32" color="warning" class="mb-2">mdi-bank</v-icon>
                    <p class="text-subtitle-1 font-weight-medium">{{ $t('home.features.virtualMoney.title') }}</p>
                    <p class="text-caption text-medium-emphasis">{{ $t('home.features.virtualMoney.description') }}</p>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Bottoni di accesso -->
            <div class="mb-8">
              <!-- Mostra bottoni login/register se non autenticato -->
              <template v-if="!isAuthenticated">
                <v-btn
                  size="large"
                  color="primary"
                  class="mr-4 mb-2"
                  @click="navigateToLogin"
                  prepend-icon="mdi-login"
                >
                  {{ $t('common.login') }}
                </v-btn>
                <v-btn
                  size="large"
                  variant="outlined"
                  color="primary"
                  class="mb-2"
                  @click="navigateToRegister"
                  prepend-icon="mdi-account-plus"
                >
                  {{ $t('common.register') }}
                </v-btn>
              </template>
              
              <!-- Mostra bottone play se autenticato -->
              <template v-else>
                <v-btn
                  size="large"
                  color="success"
                  class="mb-2"
                  @click="navigateToMyBets"
                  prepend-icon="mdi-play"
                >
                  {{ $t('home.goToMyBets') }}
                </v-btn>
              </template>
            </div>

            <!-- Call to action secondaria -->
            <div>
              <p class="text-medium-emphasis">
                {{ $t('home.callToAction') }}
              </p>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Separatore visivo -->
    <v-divider class="mb-8"></v-divider>

    <!-- Sezione Dashboard con Header -->
    <v-container fluid>
      <v-row class="mb-6">
        <v-col cols="12">
          <div class="d-flex align-center mb-4">
            <v-icon size="40" class="mr-3" color="primary">mdi-chart-line</v-icon>
            <div>
              <h2 class="text-h3 font-weight-bold">{{ $t('home.dashboard.title') }}</h2>
              <p class="text-subtitle-1 text-medium-emphasis">{{ $t('home.dashboard.subtitle') }}</p>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Componente Dashboard -->
      <Dashboard />
    </v-container>
  </main>
</template>

<style scoped>
.hero-section {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.05) 0%, rgba(var(--v-theme-secondary), 0.05) 100%);
  min-height: 80vh;
}

.min-height-section {
  min-height: 80vh;
}

.gradient-text {
  background: linear-gradient(45deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.feature-item {
  padding: 1rem;
  border-radius: 12px;
  transition: transform 0.2s ease-in-out;
  cursor: pointer;
}

.feature-item:hover {
  transform: translateY(-4px);
  background: rgba(var(--v-theme-surface-variant), 0.3);
}

.v-btn {
  text-transform: none;
  font-weight: 500;
}

/* Animazione fade-in per gli elementi */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-section {
  animation: fadeInUp 0.8s ease-out;
}
</style>
